import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  BarChart3, Users, Mail, Calendar, Search, LogOut, 
  Eye, Clock, TrendingUp, FileText, Loader2, CheckCircle2,
  Phone, Building, MessageSquare, Trash2, ExternalLink, Globe, MapPin, Play, Film
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Country flag emoji helper
const getCountryFlag = (countryCode) => {
  if (!countryCode || countryCode === 'XX') return '🌍';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

// Colors for pie chart
const COLORS = ['#000000', '#333333', '#555555', '#777777', '#999999', '#BBBBBB'];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('analytics');
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [seoSettings, setSeoSettings] = useState([]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
  });

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    verifyAndLoadData();
  }, [navigate]);

  const verifyAndLoadData = async () => {
    try {
      await axios.get(`${API}/admin/verify`, getAuthHeaders());
      loadAllData();
    } catch (err) {
      localStorage.removeItem('adminToken');
      navigate('/admin');
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, contactsRes, waitlistRes, bookingsRes, seoRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview`, getAuthHeaders()),
        axios.get(`${API}/admin/contacts`, getAuthHeaders()),
        axios.get(`${API}/admin/waitlist`, getAuthHeaders()),
        axios.get(`${API}/admin/bookings`, getAuthHeaders()),
        axios.get(`${API}/admin/seo`, getAuthHeaders())
      ]);

      setAnalytics(analyticsRes.data);
      setContacts(contactsRes.data.contacts || []);
      setWaitlist(waitlistRes.data.entries || []);
      setBookings(bookingsRes.data.bookings || []);
      setSeoSettings(seoRes.data.settings || []);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const handleUpdateContactStatus = async (contactId, status) => {
    try {
      await axios.put(`${API}/admin/contacts/${contactId}/status?status=${status}`, {}, getAuthHeaders());
      loadAllData();
    } catch (err) {
      console.error('Error updating contact:', err);
    }
  };

  const handleDeleteWaitlistEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      await axios.delete(`${API}/admin/waitlist/${entryId}`, getAuthHeaders());
      loadAllData();
    } catch (err) {
      console.error('Error deleting entry:', err);
    }
  };

  const handleUpdateSeo = async (page, settings) => {
    try {
      await axios.put(`${API}/admin/seo/${page}`, settings, getAuthHeaders());
      alert('SEO settings saved!');
      loadAllData();
    } catch (err) {
      console.error('Error updating SEO:', err);
    }
  };

  const tabs = [
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'contacts', label: 'Contacts', icon: Mail, count: contacts.filter(c => c.status === 'new').length },
    { id: 'waitlist', label: 'Waitlist', icon: Users, count: waitlist.length },
    { id: 'bookings', label: 'Bookings', icon: Calendar, count: bookings.length },
    { id: 'seo', label: 'SEO', icon: Search }
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold">LTV Production Admin</h1>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              className="border-red-200 text-red-600 hover:bg-red-50"
              onClick={() => navigate('/admin/livestream')}
            >
              <Play className="h-4 w-4 mr-2" />
              Livestream
            </Button>
            <Button 
              variant="outline" 
              className="border-purple-200 text-purple-600 hover:bg-purple-50"
              onClick={() => navigate('/admin/vod')}
            >
              <Film className="h-4 w-4 mr-2" />
              VOD
            </Button>
            <Button variant="ghost" onClick={() => window.open('/', '_blank')}>
              <ExternalLink className="h-4 w-4 mr-2" />
              View Site
            </Button>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'outline'}
                onClick={() => setActiveTab(tab.id)}
                className={activeTab === tab.id ? 'bg-black text-white' : ''}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.label}
                {tab.count > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {tab.count}
                  </span>
                )}
              </Button>
            );
          })}
        </div>

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Page Views</p>
                      <p className="text-3xl font-bold">{analytics.total_views.toLocaleString()}</p>
                    </div>
                    <Eye className="h-8 w-8 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Unique Visitors</p>
                      <p className="text-3xl font-bold">{analytics.unique_visitors.toLocaleString()}</p>
                    </div>
                    <Users className="h-8 w-8 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Views Today</p>
                      <p className="text-3xl font-bold">{analytics.views_today.toLocaleString()}</p>
                    </div>
                    <Clock className="h-8 w-8 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Views This Week</p>
                      <p className="text-3xl font-bold">{analytics.views_this_week.toLocaleString()}</p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Daily Views (Last 30 Days)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={analytics.daily_views}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="views" stroke="#000" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Popular Pages</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analytics.page_breakdown} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="page" type="category" width={100} tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Bar dataKey="views" fill="#000" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Location Analytics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Visitors by Country
                  </CardTitle>
                  <CardDescription>Where your visitors are coming from</CardDescription>
                </CardHeader>
                <CardContent>
                  {analytics.country_breakdown && analytics.country_breakdown.length > 0 ? (
                    <div className="space-y-3">
                      {analytics.country_breakdown.map((country, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{getCountryFlag(country.country_code)}</span>
                            <span className="font-medium">{country.country}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-32 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-black h-2 rounded-full" 
                                style={{ 
                                  width: `${Math.min(100, (country.views / analytics.total_views) * 100)}%` 
                                }}
                              />
                            </div>
                            <span className="text-sm text-gray-600 w-12 text-right">{country.views}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No location data yet. Visitors will be tracked from now on.</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Top Cities
                  </CardTitle>
                  <CardDescription>Most active visitor cities</CardDescription>
                </CardHeader>
                <CardContent>
                  {analytics.city_breakdown && analytics.city_breakdown.length > 0 ? (
                    <div className="space-y-3">
                      {analytics.city_breakdown.map((city, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium">{city.city}</p>
                            <p className="text-sm text-gray-500">{city.country}</p>
                          </div>
                          <div className="bg-black text-white px-3 py-1 rounded-full text-sm font-medium">
                            {city.views} visits
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No city data yet. Visitors will be tracked from now on.</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <Card>
            <CardHeader>
              <CardTitle>Contact Form Submissions</CardTitle>
              <CardDescription>{contacts.length} total submissions</CardDescription>
            </CardHeader>
            <CardContent>
              {contacts.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No contact submissions yet</p>
              ) : (
                <div className="space-y-4">
                  {contacts.map((contact) => (
                    <div key={contact.id} className={`border rounded-lg p-4 ${contact.status === 'new' ? 'bg-yellow-50 border-yellow-200' : 'bg-white'}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-bold text-lg">{contact.full_name}</h3>
                          <div className="flex flex-wrap gap-3 text-sm text-gray-600 mt-1">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" /> {contact.email}
                            </span>
                            {contact.phone_number && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" /> {contact.phone_number}
                              </span>
                            )}
                            {contact.company && (
                              <span className="flex items-center gap-1">
                                <Building className="h-3 w-3" /> {contact.company}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {contact.status === 'new' && (
                            <Button size="sm" onClick={() => handleUpdateContactStatus(contact.id, 'read')}>
                              Mark Read
                            </Button>
                          )}
                          {contact.status === 'read' && (
                            <Button size="sm" variant="outline" onClick={() => handleUpdateContactStatus(contact.id, 'contacted')}>
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Contacted
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded mt-2">
                        <p className="flex items-start gap-2">
                          <MessageSquare className="h-4 w-4 mt-1 flex-shrink-0" />
                          {contact.message}
                        </p>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">{formatDate(contact.timestamp)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Waitlist Tab */}
        {activeTab === 'waitlist' && (
          <Card>
            <CardHeader>
              <CardTitle>Miami Studio Waitlist</CardTitle>
              <CardDescription>{waitlist.length} people on the waitlist</CardDescription>
            </CardHeader>
            <CardContent>
              {waitlist.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No waitlist entries yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Name</th>
                        <th className="text-left py-3 px-2">Email</th>
                        <th className="text-left py-3 px-2">Phone</th>
                        <th className="text-left py-3 px-2">Content Type</th>
                        <th className="text-left py-3 px-2">Date</th>
                        <th className="text-left py-3 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waitlist.map((entry) => (
                        <tr key={entry.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-2 font-medium">{entry.full_name}</td>
                          <td className="py-3 px-2">{entry.email}</td>
                          <td className="py-3 px-2">{entry.phone || '-'}</td>
                          <td className="py-3 px-2 capitalize">{entry.content_type || '-'}</td>
                          <td className="py-3 px-2 text-sm text-gray-500">{formatDate(entry.timestamp)}</td>
                          <td className="py-3 px-2">
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteWaitlistEntry(entry.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <Card>
            <CardHeader>
              <CardTitle>Upcoming Bookings</CardTitle>
              <CardDescription>Consultation calls from Google Calendar</CardDescription>
            </CardHeader>
            <CardContent>
              {bookings.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No upcoming bookings</p>
              ) : (
                <div className="space-y-4">
                  {bookings.map((booking) => (
                    <div key={booking.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-bold">{booking.title}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            <Calendar className="h-3 w-3 inline mr-1" />
                            {formatDate(booking.start)}
                          </p>
                          {booking.attendees && booking.attendees.length > 0 && (
                            <p className="text-sm text-gray-600 mt-1">
                              <Users className="h-3 w-3 inline mr-1" />
                              {booking.attendees.join(', ')}
                            </p>
                          )}
                        </div>
                        {booking.meet_link && (
                          <Button size="sm" onClick={() => window.open(booking.meet_link, '_blank')}>
                            Join Meet
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* SEO Tab */}
        {activeTab === 'seo' && (
          <div className="space-y-6">
            {seoSettings.map((setting) => (
              <SeoCard key={setting.page} setting={setting} onSave={handleUpdateSeo} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// SEO Card Component
const SeoCard = ({ setting, onSave }) => {
  const [form, setForm] = useState({
    title: setting.title || '',
    description: setting.description || '',
    keywords: setting.keywords || ''
  });

  const pageNames = {
    home: 'Homepage',
    about: 'About Us',
    miami: 'Miami Studio',
    'on-location': 'On-Location Production'
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="capitalize">{pageNames[setting.page] || setting.page}</CardTitle>
        <CardDescription>SEO settings for /{setting.page === 'home' ? '' : setting.page}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-sm font-medium">Meta Title</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full mt-1 px-3 py-2 border rounded-lg"
            placeholder="Page title for search engines"
          />
          <p className="text-xs text-gray-500 mt-1">{form.title.length}/60 characters</p>
        </div>
        <div>
          <label className="text-sm font-medium">Meta Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full mt-1 px-3 py-2 border rounded-lg"
            rows={3}
            placeholder="Brief description for search results"
          />
          <p className="text-xs text-gray-500 mt-1">{form.description.length}/160 characters</p>
        </div>
        <div>
          <label className="text-sm font-medium">Keywords</label>
          <input
            type="text"
            value={form.keywords}
            onChange={(e) => setForm({ ...form, keywords: e.target.value })}
            className="w-full mt-1 px-3 py-2 border rounded-lg"
            placeholder="video production, podcast studio, content creation"
          />
        </div>
        <Button onClick={() => onSave(setting.page, form)} className="bg-black hover:bg-gray-800 text-white">
          <FileText className="h-4 w-4 mr-2" />
          Save SEO Settings
        </Button>
      </CardContent>
    </Card>
  );
};

export default AdminDashboard;
